import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import RangePicker from '@components/V4/RangePicker/RangePicker';
import Select, { SelectOption } from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useDebounce } from '@hooks';
import { TriState } from '@shared/clientTypes/misc';
import { FLAG_TYPE } from '@shared/constants';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { useGetAllMentors, useGetAllStudents } from 'apps/agora/src/actions';
import { useGetAllCourses } from 'apps/agora/src/actions/courses.actions';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { COLORS } from 'apps/agora/src/v3/global';
import { Moment } from 'moment';
import { useState } from 'react';

const PRIORITIZED_FLAG_LABELS = [
  'First meeting not scheduled in 5 days',
  'First meeting not held in 10 days',
];

const DEPRECATED_FLAG_LABELS = [
  'First meeting not scheduled in 3 days',
  'First meeting not held in 7 days',
];

const FLAG_TYPE_OPTIONS: SelectOption[] = (() => {
  const baseOptions: SelectOption[] = FLAG_TYPE.map((flag, index) => ({
    value: index.toString(),
    label: flag,
  }));

  const prioritizedOptions = PRIORITIZED_FLAG_LABELS.map((label) => {
    return baseOptions.find((option) => option.label === label);
  }).filter(Boolean) as SelectOption[];

  const deprecatedOptions = DEPRECATED_FLAG_LABELS.map((label) => {
    return baseOptions.find((option) => option.label === label);
  }).filter(Boolean) as SelectOption[];

  const middleOptions = baseOptions.filter(
    (option) =>
      !PRIORITIZED_FLAG_LABELS.includes(option.label) &&
      !DEPRECATED_FLAG_LABELS.includes(option.label)
  );

  return [...prioritizedOptions, ...middleOptions, ...deprecatedOptions];
})();

type Props = {
  search?: string;
  flagTypeHandler: (flagType: string | undefined) => void;
  courseIdHandler: (courseId: any) => void;
  studentIdHandler: (studentId: any) => void;
  mentorIdHandler: (mentorId: any) => void;
  searchHandler: (searchValue: string) => void;
  activeFlagsHandler: (active: string) => void;
  userStatusHandler: (userStatus: string) => void;
  selectedTab: string;
  selectedTabHandler: (tab: string) => void;
  flagType?: string;
  courseId?: string;
  studentId?: string;
  mentorId?: string;
  isActive: string;
  getRangePickerValue: () => [Moment, Moment] | null;
  rangePickerChangeHandler: (dates: [Moment | null, Moment | null]) => void;
};

const tabs = [
  { name: 'Flags', current: false, key: 'Flags' },
  { name: 'Overview', current: false, key: 'Overview' },
  { name: 'Mentors Health', current: false, key: 'MentorsHealth' },
  { name: 'Students Health', current: false, key: 'StudentsHealth' },
];

const FlagsTableHeader = (props: Props) => {
  const {
    search,
    flagTypeHandler,
    courseIdHandler,
    studentIdHandler,
    mentorIdHandler,
    searchHandler,
    activeFlagsHandler,
    userStatusHandler,
    selectedTab,
    selectedTabHandler,
    flagType,
    courseId,
    studentId,
    mentorId,
    isActive,
    getRangePickerValue,
    rangePickerChangeHandler,
  } = props;

  const { data: allCourses } = useGetAllCourses();

  const [checkedState, setCheckedState] = useState<TriState>('unchecked');
  const [studentSearch, setStudentSearch] = useState('');
  const [mentorSearch, setMentorSearch] = useState('');
  const debouncedStudentSearch = useDebounce(studentSearch ?? '', 500);
  const debouncedMentorSearch = useDebounce(mentorSearch ?? '', 500);
  const allOption = { label: 'ALL', value: null };

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const { data: mentors, isLoading: isMentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    limit: 20,
  });

  const getStudentOptions = () => {
    const studentOptions: SelectOption[] =
      students?.map((student) => ({
        label: student.fullName,
        value: student._id,
      })) || [];

    if (!debouncedStudentSearch) {
      studentOptions.unshift(allOption);
    }

    return studentOptions;
  };

  const getMentorOptions = () => {
    const mentorOptions: SelectOption[] =
      mentors?.map((mentor) => ({
        label: mentor.fullName,
        value: mentor._id,
      })) || [];

    if (!debouncedMentorSearch) {
      mentorOptions.unshift(allOption);
    }

    return mentorOptions;
  };

  const getTabClassName = (tab: typeof tabs[0]) => {
    const isSelected = tab?.key === selectedTab;
    return mergeClassNames(
      'w-1/4 border-b-2 py-4 px-1 text-sm font-medium whitespace-nowrap cursor-pointer max-w-[150px] text-center',
      isSelected
        ? '' // Colors will be handled by inline styles since they're custom theme colors
        : 'border-transparent hover:border-gray-300 hover:text-gray-700'
    );
  };

  const getTabStyle = (tab: typeof tabs[0]) => {
    const isSelected = tab?.key === selectedTab;
    return {
      color: isSelected ? COLORS.PRIMARY : COLORS.TEXT_SECONDARY,
      borderColor: isSelected ? COLORS.PRIMARY : COLORS.DARK_GREY_ALT,
    };
  };

  const getNotFoundContent = (isLoading: boolean) => {
    if (isLoading) {
      return <LoadingSpinner className="mx-auto" size="normal" />;
    }
    return null;
  };

  const studentSearchChangeHandler = (value: string) => {
    setStudentSearch(value);
  };

  const mentorSearchChangeHandler = (value: string) => {
    setMentorSearch(value);
  };

  const studentSearchBlurHandler = () => {
    setStudentSearch('');
  };

  const mentorSearchBlurHandler = () => {
    setMentorSearch('');
  };

  const testUsersTriStateChangeHandler = (value: TriState) => {
    setCheckedState(value);
    userStatusHandler(value);
  };

  const flagsTriStateChangeHandler = (value: TriState) => {
    if (value === 'checked') {
      activeFlagsHandler('0');
    } else if (value === 'unchecked') {
      activeFlagsHandler('1');
    } else {
      activeFlagsHandler('2');
    }
  };

  const getFlagsToggleValue = () =>
    isActive === '0' ? 'checked' : isActive === '1' ? 'unchecked' : 'indeterminate';

  return (
    <div style={{ backgroundColor: COLORS.DARK_GREY_ALT }} className="pt-6 px-8">
      <div className="flex flex-col w-full">
        <div className="flex justify-between items-center mb-6">
          <h1 className="header-title text-white">Flags Dashboard</h1>
          <div className="w-96">
            <AgoraSearchBar
              search={search}
              handleSearch={searchHandler}
              placeholder={'Search by student, mentor, enrolment or keywords...'}
            />
          </div>
        </div>
        <div>
          <div className="flex flex-col">
            <div className="flex flex-wrap gap-4">
              <Select
                className="!w-48"
                label="Flag Type"
                value={flagType}
                options={FLAG_TYPE_OPTIONS}
                onSelect={(value) => flagTypeHandler(value as string)}
                placeholder="Search Flag Type"
                variant="secondary"
                showMultiline
              />

              <Select
                className="!w-48"
                label="Course Flagged"
                value={courseId}
                options={allCourses?.map((course) => ({
                  value: course._id,
                  label: course.title,
                }))}
                onSelect={(value) => courseIdHandler(value as string)}
                placeholder="Search Course Flagged"
                variant="secondary"
              />

              <Select
                className="!w-48"
                label="Students Flagged"
                value={studentId}
                filterOption={() => true}
                searchValue={studentSearch}
                onSearch={studentSearchChangeHandler}
                onBlur={studentSearchBlurHandler}
                onSelect={(value) => studentIdHandler(value)}
                options={getStudentOptions()}
                notFoundContent={getNotFoundContent(isStudentsLoading)}
                placeholder="Search Students"
                variant="secondary"
              />

              <Select
                className="!w-48"
                label="Mentor Flagged"
                value={mentorId}
                filterOption={() => true}
                searchValue={mentorSearch}
                onSearch={mentorSearchChangeHandler}
                onSelect={(value) => mentorIdHandler(value)}
                onBlur={mentorSearchBlurHandler}
                options={getMentorOptions()}
                notFoundContent={getNotFoundContent(isMentorsLoading)}
                placeholder="Search Mentor"
                variant="secondary"
              />
              {!['MentorsHealth', 'StudentsHealth'].includes(selectedTab) && (
                <RangePicker
                  variant="secondary"
                  label="Date Range"
                  className="w-48"
                  value={getRangePickerValue()}
                  onChange={rangePickerChangeHandler}
                />
              )}
            </div>
            <div className="flex items-center gap-4 mt-3">
              <TriStateToggle
                title="Show Users:"
                options={[
                  { label: 'Regular', value: 'unchecked' },
                  { label: 'Test', value: 'checked' },
                  { label: 'All', value: 'indeterminate' },
                ]}
                value={checkedState}
                onChange={testUsersTriStateChangeHandler}
              />
              {(selectedTab === 'Flags' || selectedTab === 'Overview') && (
                <TriStateToggle
                  title="Show Flags:"
                  options={[
                    { label: 'Active', value: 'checked' },
                    { label: 'Closed', value: 'unchecked' },
                    { label: 'All', value: 'indeterminate' },
                  ]}
                  value={getFlagsToggleValue()}
                  onChange={flagsTriStateChangeHandler}
                />
              )}
            </div>

            <div className="mt-3">
              <div className="hidden sm:block">
                <div>
                  <nav className="-mb-px flex" aria-label="Tabs">
                    {tabs.map((tab) => (
                      <span
                        style={getTabStyle(tab)}
                        key={tab?.key}
                        onClick={() => selectedTabHandler(tab?.key)}
                        className={getTabClassName(tab)}
                        aria-current={tab?.current ? 'page' : undefined}
                      >
                        {tab?.name}
                      </span>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlagsTableHeader;
